/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Indexable } from '@yorkie-js/sdk/src/document/document';
import { Checkpoint } from '@yorkie-js/sdk/src/document/change/checkpoint';
import { Change } from '@yorkie-js/sdk/src/document/change/change';
import { VersionVector } from '../time/version_vector';

/**
 * `ChangePack` is a unit for delivering changes in a document to the remote.
 *
 */
export class ChangePack<P extends Indexable> {
  /**
   * `documentKey` is the key of the document.
   */
  private documentKey: string;

  /**
   * `Checkpoint` is used to determine the client received changes.
   */
  private checkpoint: Checkpoint;
  /**
   * `isRemoved` is a flag that indicates whether the document is removed.
   */
  private isRemoved: boolean;

  private changes: Array<Change<P>>;

  /**
   * `snapshot` is a byte array that encodes the document.
   */
  private snapshot?: Uint8Array;

  /**
   * `versionVector` is the version vector current document
   */
  private versionVector?: VersionVector;

  /**
   * `epoch` is the document's compaction epoch. It is a bidirectional carrier:
   * server responses set it to the document's current epoch, and an attach/sync
   * request presents the client's last-known epoch so the server can detect a
   * stale-epoch mismatch after a force compaction.
   */
  private epoch: bigint;

  constructor(
    key: string,
    checkpoint: Checkpoint,
    isRemoved: boolean,
    changes: Array<Change<P>>,
    versionVector?: VersionVector,
    snapshot?: Uint8Array,
    epoch: bigint = 0n,
  ) {
    this.documentKey = key;
    this.checkpoint = checkpoint;
    this.isRemoved = isRemoved;
    this.changes = changes;
    this.snapshot = snapshot;
    this.versionVector = versionVector;
    this.epoch = epoch;
  }
  /**
   * `create` creates a new instance of ChangePack.
   */
  public static create<P extends Indexable>(
    key: string,
    checkpoint: Checkpoint,
    isRemoved: boolean,
    changes: Array<Change<P>>,
    versionVector?: VersionVector,
    snapshot?: Uint8Array,
    epoch: bigint = 0n,
  ): ChangePack<P> {
    return new ChangePack<P>(
      key,
      checkpoint,
      isRemoved,
      changes,
      versionVector,
      snapshot,
      epoch,
    );
  }

  /**
   * `getDocumentKey` returns the document key of this pack.
   */
  public getDocumentKey(): string {
    return this.documentKey;
  }

  /**
   * `getCheckpoint` returns the checkpoint of this pack.
   */
  public getCheckpoint(): Checkpoint {
    return this.checkpoint;
  }

  /**
   * `getIsRemoved` returns the whether this document is removed.
   */
  public getIsRemoved(): boolean {
    return this.isRemoved;
  }

  /**
   * `getChanges` returns the changes of this pack.
   */
  public getChanges(): Array<Change<P>> {
    return this.changes;
  }

  /**
   * `hasChanges` returns the whether this pack has changes or not.
   */
  public hasChanges(): boolean {
    return this.changes.length > 0;
  }

  /**
   * `getChangeSize` returns the size of changes this pack has.
   */
  public getChangeSize(): number {
    return this.changes.length;
  }

  /**
   * `hasSnapshot` returns the whether this pack has a snapshot or not.
   */
  public hasSnapshot(): boolean {
    return !!this.snapshot && !!this.snapshot.length;
  }

  /**
   * `getSnapshot` returns the snapshot of this pack.
   */
  public getSnapshot(): Uint8Array | undefined {
    return this.snapshot;
  }

  /**
   * `getVersionVector` returns the document's version vector of this pack
   */
  public getVersionVector(): VersionVector | undefined {
    return this.versionVector;
  }

  /**
   * `getEpoch` returns the document's compaction epoch of this pack.
   */
  public getEpoch(): bigint {
    return this.epoch;
  }
}
